/**
 * SegmentedButton atom — exclusive single-select on a shared track.
 * Controlled only. Filtering / GTM / domain chrome stay with the consumer.
 *
 * Axes: appearance × intent × size (Button-aligned intents).
 * Semantics: radiogroup + radio (not tablist).
 */

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { Tooltip } from '../../tooltip';
import {
  getSegmentedIconClassName,
  getSegmentedOptionClassName,
  getSegmentedSlideIndicatorClassName,
  getSegmentedTrackClassName,
} from './SegmentedButton.styles';
import type { SegmentedButtonProps } from './SegmentedButton.types';

function enabledIndices(options: SegmentedButtonProps['options']): number[] {
  return options.reduce<number[]>((acc, option, index) => {
    if (!option.disabled) acc.push(index);
    return acc;
  }, []);
}

function measureOption(
  option: HTMLButtonElement | null,
  track: HTMLDivElement | null,
) {
  if (!option || !track) return null;

  // Prefer layout offsets: absolute `left`/`top` are relative to the track's
  // padding box. getBoundingClientRect() is border-box and can skew the pill
  // (looks shifted vs hover) when the track has a border or sits in a scroller.
  if (option.offsetParent === track) {
    return {
      left: option.offsetLeft,
      top: option.offsetTop,
      width: option.offsetWidth,
      height: option.offsetHeight,
    };
  }

  const optionRect = option.getBoundingClientRect();
  const trackRect = track.getBoundingClientRect();
  return {
    left: optionRect.left - trackRect.left - track.clientLeft,
    top: optionRect.top - trackRect.top - track.clientTop,
    width: option.offsetWidth || optionRect.width,
    height: option.offsetHeight || optionRect.height,
  };
}

export const SegmentedButton: React.FC<SegmentedButtonProps> = ({
  value,
  onValueChange,
  options,
  'aria-label': ariaLabel,
  size = 'md',
  appearance = 'raised',
  intent = 'secondary',
  equalWidth = false,
  rounded = 'full',
  indicator = 'slide',
  track = true,
  className,
}) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const hasSlide = indicator === 'slide';
  const previousValueRef = useRef(value);
  const [pill, setPill] = useState<ReturnType<typeof measureOption>>(null);
  const [slideEnabled, setSlideEnabled] = useState(false);

  const focusAndSelect = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.disabled) return;
      buttonRefs.current[index]?.focus();
      if (option.value !== value) {
        onValueChange(option.value);
      }
    },
    [options, value, onValueChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const enabled = enabledIndices(options);
      if (enabled.length === 0) return;

      const currentIndex = options.findIndex((option) => option.value === value);
      const position = Math.max(0, enabled.indexOf(currentIndex));
      const last = enabled.length - 1;

      let nextPosition: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextPosition = position >= last ? 0 : position + 1;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextPosition = position <= 0 ? last : position - 1;
          break;
        case 'Home':
          nextPosition = 0;
          break;
        case 'End':
          nextPosition = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      focusAndSelect(enabled[nextPosition]);
    },
    [focusAndSelect, options, value],
  );

  const selectedIndex = options.findIndex((entry) => entry.value === value);
  const fallbackIndex = enabledIndices(options)[0] ?? -1;
  const pillIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;
  const activeTabIndex =
    selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : fallbackIndex;

  const applyMeasure = useCallback((withSlide: boolean) => {
    const next = measureOption(
      buttonRefs.current[pillIndex] ?? null,
      trackRef.current,
    );
    setSlideEnabled(withSlide && next != null);
    setPill(next);
  }, [pillIndex]);

  useLayoutEffect(() => {
    if (!hasSlide) {
      setPill(null);
      setSlideEnabled(false);
      return;
    }
    const valueChanged = previousValueRef.current !== value;
    previousValueRef.current = value;
    applyMeasure(valueChanged);
    const track = trackRef.current;
    const activeOption = buttonRefs.current[pillIndex] ?? null;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => applyMeasure(false));
    observer.observe(track);
    if (activeOption) observer.observe(activeOption);
    return () => observer.disconnect();
  }, [applyMeasure, hasSlide, pillIndex, value]);

  return (
    <div
      ref={trackRef}
      role="radiogroup"
      aria-label={ariaLabel}
      data-slot="segmented-button"
      data-appearance={appearance}
      data-intent={intent}
      data-size={size}
      data-rounded={rounded}
      data-equal-width={equalWidth ? 'true' : undefined}
      data-indicator={indicator}
      className={getSegmentedTrackClassName(
        size,
        rounded,
        equalWidth,
        appearance,
        intent,
        className,
        track,
      )}
      onKeyDown={handleKeyDown}
    >
      {hasSlide && pill ? (
        <span
          aria-hidden
          className={cn(
            getSegmentedSlideIndicatorClassName(rounded, appearance, intent),
            slideEnabled && 'transition-[left,top,width,height] duration-300 ease-out',
          )}
          style={{ left: pill.left, top: pill.top, width: pill.width, height: pill.height }}
          onTransitionEnd={() => setSlideEnabled(false)}
        />
      ) : null}
      {options.map((option, index) => {
        const isActive = option.value === value;
        const disabled = Boolean(option.disabled);
        const accessibleName =
          option.title ??
          (typeof option.label === 'string' ? option.label : undefined);

        const optionButton = (
          <button
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            data-slot="segmented-button-option"
            data-active={isActive ? 'true' : undefined}
            aria-label={accessibleName}
            aria-checked={isActive}
            aria-disabled={disabled || undefined}
            tabIndex={index === activeTabIndex ? 0 : -1}
            disabled={disabled}
            {...option.dataAttributes}
            className={getSegmentedOptionClassName(
              size,
              rounded,
              appearance,
              intent,
              equalWidth,
              isActive,
              disabled,
              hasSlide,
            )}
            onClick={() => {
              if (!disabled && option.value !== value) {
                onValueChange(option.value);
              }
            }}
          >
            {option.icon ? (
              <span data-slot="segmented-button-icon" className={cn('relative z-10', getSegmentedIconClassName(size))}>
                {option.icon}
              </span>
            ) : null}

            {option.label != null && option.label !== '' ? (
              <span data-slot="segmented-button-label" className="relative z-10 inline-flex items-center truncate leading-none">
                {option.label}
              </span>
            ) : null}
          </button>
        );

        if (!option.title) {
          return <React.Fragment key={option.value}>{optionButton}</React.Fragment>;
        }

        return (
          <Tooltip
            key={option.value}
            content={option.title}
            variant="bottom"
            triggerClassName={equalWidth ? 'flex-1 min-w-0' : undefined}
          >
            {optionButton}
          </Tooltip>
        );
      })}
    </div>
  );
};

SegmentedButton.displayName = 'SegmentedButton';
